import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Sparkles } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  INFRA_SECTIONS,
  HIGHEST_LEVERAGE,
  type ItemStatus,
} from "@/data/infraChecklist";

// v2: only user overrides are persisted. Defaults always come from code so
// updates to defaultStatus/defaultNote in infraChecklist.ts show up immediately
// without being shadowed by a stale localStorage snapshot.
const STORAGE_KEY = "emrkt_infra_checklist_v2";
const LEGACY_STORAGE_KEY = "emrkt_infra_checklist_v1";

type Overrides = Record<string, ItemStatus>;
type StateMap = Record<string, { status: ItemStatus; note?: string }>;

const buildState = (overrides: Overrides): StateMap => {
  const state: StateMap = {};
  for (const section of INFRA_SECTIONS) {
    for (const item of section.items) {
      state[item.key] = {
        status: overrides[item.key] ?? item.defaultStatus ?? "todo",
        note: item.defaultNote,
      };
    }
  }
  return state;
};

const loadOverrides = (): Overrides => {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Overrides;
    // One-time cleanup of the old full-state key.
    if (localStorage.getItem(LEGACY_STORAGE_KEY)) {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
    return {};
  } catch {
    return {};
  }
};

const statusPillClass = (status: ItemStatus) => {
  switch (status) {
    case "done":
      return "bg-primary/10 text-primary";
    case "in_progress":
      return "bg-amber-500/10 text-amber-500";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const statusLabel = (status: ItemStatus) =>
  status === "in_progress" ? "in progress" : status;

const InfraChecklist = () => {
  const [overrides, setOverrides] = useState<Overrides>(loadOverrides);
  const state = useMemo(() => buildState(overrides), [overrides]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    } catch {
      /* ignore quota errors */
    }
  }, [overrides]);

  const { total, done } = useMemo(() => {
    let t = 0;
    let d = 0;
    for (const section of INFRA_SECTIONS) {
      for (const item of section.items) {
        t += 1;
        if (state[item.key]?.status === "done") d += 1;
      }
    }
    return { total: t, done: d };
  }, [state]);

  const percent = total === 0 ? 0 : Math.round((done / total) * 100);

  const setStatus = (key: string, next: ItemStatus) => {
    setOverrides((prev) => ({ ...prev, [key]: next }));
  };

  const cycleStatus = (key: string) => {
    const current = state[key]?.status ?? "todo";
    const next: ItemStatus =
      current === "todo"
        ? "in_progress"
        : current === "in_progress"
        ? "done"
        : "todo";
    setStatus(key, next);
  };

  const toggleDone = (key: string, checked: boolean) => {
    setStatus(key, checked ? "done" : "todo");
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold mb-1">
          Infrastructure Roadmap
        </h2>
        <p className="text-sm text-muted-foreground">
          Strategic backlog for emrkt.co — check items as they ship. Saved locally to this browser.
        </p>
      </div>

      <div className="bg-card border border-border rounded-[0.875rem] p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium">Overall progress</span>
          <span className="text-sm text-muted-foreground">
            {done} of {total} complete · {percent}%
          </span>
        </div>
        <Progress value={percent} className="h-2" />
      </div>

      <div className="bg-primary/5 border border-primary/20 rounded-[0.875rem] p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <h3 className="font-display font-semibold text-sm">Highest leverage next</h3>
        </div>
        <ul className="space-y-1.5 text-sm text-muted-foreground">
          {HIGHEST_LEVERAGE.map((item) => (
            <li key={item.key} className="flex gap-2">
              <span className="text-primary">→</span>
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-3">
        {INFRA_SECTIONS.map((section) => {
          const sectionTotal = section.items.length;
          const sectionDone = section.items.filter(
            (item) => state[item.key]?.status === "done"
          ).length;
          const allDone = sectionDone === sectionTotal;
          return (
            <Collapsible
              key={section.id}
              defaultOpen={!allDone}
              className="bg-card border border-border rounded-[0.875rem] overflow-hidden"
            >
              <CollapsibleTrigger className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors group">
                <div className="flex items-center gap-3">
                  <h3 className="font-display font-semibold text-sm text-left">
                    {section.title}
                  </h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      allDone
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {sectionDone}/{sectionTotal}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <ul className="border-t border-border divide-y divide-border">
                  {section.items.map((item) => {
                    const entry = state[item.key] ?? { status: "todo" as ItemStatus };
                    const isDone = entry.status === "done";
                    return (
                      <li
                        key={item.key}
                        className="flex items-start gap-3 p-4 hover:bg-muted/20 transition-colors"
                      >
                        <Checkbox
                          checked={isDone}
                          onCheckedChange={(checked) =>
                            toggleDone(item.key, checked === true)
                          }
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <span
                              className={`text-sm ${
                                isDone
                                  ? "line-through text-muted-foreground"
                                  : "text-foreground"
                              }`}
                            >
                              {item.label}
                            </span>
                            <button
                              type="button"
                              onClick={() => cycleStatus(item.key)}
                              className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 ${statusPillClass(
                                entry.status
                              )}`}
                              title="Click to cycle status"
                            >
                              {statusLabel(entry.status)}
                            </button>
                          </div>
                          {entry.note && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {entry.note}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
};

export default InfraChecklist;